import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AppSvgIconComponent, SignalButton } from '@vallift/ngl';
import { environment } from '@environments/environment';

export interface LocationDetailsPreviewOverlayData {
  display: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string | null;
  /** When true, show as mobile sheet (close via DialogRef). */
  sheetMode?: boolean;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onClose?: () => void;
}

@Component({
  selector: 'app-location-details-preview-overlay',
  standalone: true,
  imports: [AppSvgIconComponent, SignalButton],
  templateUrl: './location-details-preview-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '(mouseenter)': 'onCardEnter()',
    '(mouseleave)': 'onCardLeave()',
  },
})
export class LocationDetailsPreviewOverlayComponent {
  private readonly data = inject<LocationDetailsPreviewOverlayData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef<void>, { optional: true });

  readonly sheetMode = !!this.data.sheetMode;
  readonly displayName = this.data.display?.trim() || '—';
  readonly fullAddress = this.data.address?.trim() || '—';
  readonly latitude = this.data.latitude;
  readonly longitude = this.data.longitude;
  readonly placeId = this.data.placeId?.trim() || null;

  readonly copyFeedback = signal<'address' | 'placeId' | null>(null);

  readonly hasCoordinates = computed(
    () =>
      this.latitude != null &&
      Number.isFinite(this.latitude) &&
      this.longitude != null &&
      Number.isFinite(this.longitude),
  );

  readonly staticMapUrl = computed(() => {
    if (!this.hasCoordinates()) {
      return null;
    }
    const key = environment.googleMapsApiKey?.trim();
    if (!key) {
      return null;
    }
    const lat = this.latitude!;
    const lng = this.longitude!;
    const params = new URLSearchParams({
      center: `${lat},${lng}`,
      zoom: '15',
      size: '560x220',
      scale: '2',
      maptype: 'roadmap',
      markers: `color:0xEF4444|${lat},${lng}`,
      key,
    });
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  });

  readonly mapsUrl = computed(() => {
    if (this.placeId) {
      return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(this.placeId)}`;
    }
    if (this.hasCoordinates()) {
      return `https://www.google.com/maps/search/?api=1&query=${this.latitude},${this.longitude}`;
    }
    if (this.data.address?.trim()) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.data.address.trim())}`;
    }
    return null;
  });

  onCardEnter(): void {
    this.data.onPointerEnter?.();
  }

  onCardLeave(): void {
    this.data.onPointerLeave?.();
  }

  close(): void {
    this.data.onClose?.();
    this.dialogRef?.close();
  }

  async copyText(kind: 'address' | 'placeId', text: string): Promise<void> {
    const value = text.trim();
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      this.copyFeedback.set(kind);
      window.setTimeout(() => {
        if (this.copyFeedback() === kind) {
          this.copyFeedback.set(null);
        }
      }, 1500);
    } catch {
      // Ignore clipboard failures.
    }
  }

  openInMaps(): void {
    const url = this.mapsUrl();
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  formatCoordinate(value: number | null): string {
    if (value == null || !Number.isFinite(value)) {
      return '—';
    }
    return String(value);
  }
}
